// /chatbot/test-console — drive one simulated turn end to end: text, voice,
// attachments, the live chain-of-thought stream, and what to do with the turn.

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { motion } from "framer-motion";

import { http, qs, stream } from "../api";
import { useSession } from "../App";
import { Badge, Banner, Dialog, DialogFooter, Empty, Field, Spinner, useToast } from "../ui";
import { ErrorBanner, errMessage, navigate } from "./shared";
import { DebugPanel } from "./console_debug";
import { TurnAnalysis, type LabRun } from "./console_analysis";
import {
  MAX_MEDIA_BYTES,
  base64ToBlob,
  fmtMs,
  isBot,
  isVoiceReply,
  pickRecordingMime,
  sleep,
  revealDelay,
  stageLabel,
  type AccountLite,
  type AccountsResponse,
  type SimulationMessage,
  type SimulationPayload,
  type SpeakResponse,
  type Thinking,
} from "./console_model";

export function TestConsoleView() {
  const { can } = useSession();
  const toast = useToast();
  // EITHER GRANT RUNS A CONVERSATION. The `tester` role holds `test_console` and no settings at all;
  // gating this on `settings.chatbot` alone opened the console to them and disabled every button in it.
  const canWrite = can("test_console", "write") || can("settings.chatbot", "write");
  // Model Lab is a governance screen a tester cannot open, so its buttons are not offered to them.
  const canLab = can("settings.chatbot");

  const [accounts, setAccounts] = useState<AccountLite[]>([]);
  const [accountsError, setAccountsError] = useState<unknown>(null);
  const [setup, setSetup] = useState({
    whatsapp_account: "",
    phone: "+919999000444",
    profile_name: "QA Test User",
    language: "en",
  });

  const [payload, setPayload] = useState<SimulationPayload | null>(null);
  const [composer, setComposer] = useState("");
  const [evalNote, setEvalNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [acting, setActing] = useState(false);

  const simulation = payload?.simulation;
  const messages = useMemo(() => payload?.messages ?? [], [payload]);

  const latestUserMessage = [...messages].reverse().find((m) => m.role === "user");
  const latestBotMessage = [...messages].reverse().find(isBot);

  // ---- staggered reveal --------------------------------------------------
  const [visibleCount, setVisibleCount] = useState(0);
  const staggering = useRef(false);
  const [optimisticUser, setOptimisticUser] = useState<string | null>(null);
  const [optimisticMedia, setOptimisticMedia] = useState<{
    url: string;
    kind: "audio" | "image" | "document";
    filename: string;
    caption: string;
  } | null>(null);
  const [thinking, setThinking] = useState<Thinking | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // A load or refresh reveals everything at once; only a send staggers.
    if (!staggering.current) setVisibleCount(messages.length);
  }, [messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [visibleCount, thinking, optimisticUser, optimisticMedia]);

  const displayed = messages.slice(0, visibleCount);

  // ---- accounts ----------------------------------------------------------
  useEffect(() => {
    void http
      .get<AccountsResponse>("/accounts")
      .then((res) => setAccounts(res.accounts ?? []))
      .catch((err: unknown) => setAccountsError(err));
  }, []);

  // ---- inbound media -----------------------------------------------------
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [mediaLoading, setMediaLoading] = useState<Record<string, boolean>>({});
  const mediaUrlsRef = useRef(mediaUrls);
  mediaUrlsRef.current = mediaUrls;

  useEffect(() => {
    for (const m of messages) {
      if (!m.media_url || mediaUrlsRef.current[m.id]) continue;
      setMediaLoading((s) => ({ ...s, [m.id]: true }));
      void http
        .raw(`/media/${m.id}`)
        .then(async (res) => {
          const url = URL.createObjectURL(await res.blob());
          setMediaUrls((s) => ({ ...s, [m.id]: url }));
        })
        .catch(() => {
          // Leave it unloaded — the bubble shows a placeholder instead.
        })
        .finally(() => setMediaLoading((s) => ({ ...s, [m.id]: false })));
    }
  }, [messages]);

  // ---- spoken replies ----------------------------------------------------
  const [ttsCache, setTtsCache] = useState<Record<string, string>>({});
  const [ttsLoading, setTtsLoading] = useState<Record<string, boolean>>({});
  const [ttsPlaying, setTtsPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setTtsPlaying(null);
  }, []);

  const playReply = async (m: SimulationMessage) => {
    if (!simulation) return;
    if (ttsPlaying === m.id) {
      stopPlayback();
      return;
    }
    stopPlayback();
    let url = ttsCache[m.id];
    if (!url) {
      if (ttsLoading[m.id]) return;
      setTtsLoading((s) => ({ ...s, [m.id]: true }));
      try {
        const res = await http.post<SpeakResponse>(`/chatbot/simulations/${simulation.id}/messages/${m.id}/speak`, {});
        if (!res.audio_base64) throw new Error("no audio");
        url = URL.createObjectURL(base64ToBlob(res.audio_base64, res.audio_mime_type || "audio/wav"));
        setTtsCache((s) => ({ ...s, [m.id]: url as string }));
      } catch (err) {
        toast.push(errMessage(err, "Could not play the voice reply"), "critical");
        return;
      } finally {
        setTtsLoading((s) => ({ ...s, [m.id]: false }));
      }
    }
    if (!audioRef.current) audioRef.current = new Audio();
    audioRef.current.src = url;
    audioRef.current.onended = () => setTtsPlaying(null);
    setTtsPlaying(m.id);
    try {
      await audioRef.current.play();
    } catch {
      setTtsPlaying(null);
    }
  };

  // ---- recording ---------------------------------------------------------
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | undefined>(undefined);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const releaseStream = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = undefined;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  // Blob URLs and the microphone are process-level resources; a screen change
  // must give them back.
  useEffect(
    () => () => {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try {
          recorderRef.current.stop();
        } catch {
          /* already stopping */
        }
      }
      releaseStream();
      stopPlayback();
      Object.values(mediaUrlsRef.current).forEach((u) => URL.revokeObjectURL(u));
    },
    [releaseStream, stopPlayback]
  );

  const clearOptimisticMedia = useCallback(() => {
    setOptimisticMedia((m) => {
      if (m) URL.revokeObjectURL(m.url);
      return null;
    });
  }, []);

  // ---- chain-of-thought stream ------------------------------------------
  const abortStream = useRef<(() => void) | null>(null);

  const onChainEvent = useCallback((type: string, raw: unknown) => {
    const data = (raw ?? {}) as { stage?: string; latency_ms?: number; attributes?: { summary?: string } };
    setThinking((prev) => {
      if (!prev) return prev;
      if (type === "stage_started" && data.stage) {
        return { ...prev, steps: [...prev.steps, { stage: data.stage, status: "running" }] };
      }
      if (type === "stage_completed" && data.stage) {
        let patched = false;
        const steps = [...prev.steps];
        for (let i = steps.length - 1; i >= 0; i--) {
          if (steps[i].stage === data.stage && steps[i].status === "running") {
            steps[i] = { ...steps[i], status: "done", latency_ms: data.latency_ms, note: data.attributes?.summary };
            patched = true;
            break;
          }
        }
        if (!patched) {
          steps.push({ stage: data.stage, status: "done", latency_ms: data.latency_ms, note: data.attributes?.summary });
        }
        return { ...prev, steps, totalMs: steps.reduce((a, s) => a + Number(s.latency_ms ?? 0), 0) };
      }
      if (type === "source_selected") return { ...prev, sources: prev.sources + 1 };
      if ((type === "run_completed" || type === "final_response") && data.latency_ms) {
        return { ...prev, totalMs: Number(data.latency_ms) };
      }
      return prev;
    });
  }, []);

  /** Live trace of the same turn. Best effort — it never blocks the reply. */
  const startChainStream = (message: string, source: string): Promise<void> =>
    new Promise<void>((resolve) => {
      abortStream.current?.();
      abortStream.current = stream(
        "/chatbot/model-lab/run/stream",
        { message, message_type: "text", metadata: { source } },
        (event, data) => {
          try {
            const frame = JSON.parse(data) as { type?: string; data?: unknown };
            onChainEvent(frame.type ?? event, frame.data ?? frame);
          } catch {
            /* a frame we cannot read is not worth failing the turn for */
          }
        },
        () => {
          abortStream.current = null;
          resolve();
        }
      );
    });

  /** Reveal the turn's new messages one at a time so replies stream in. */
  const revealTurn = async (next: SimulationPayload, prevCount: number) => {
    staggering.current = true;
    setPayload(next);
    setOptimisticUser(null);
    clearOptimisticMedia();
    const all = next.messages ?? [];
    let i = prevCount;
    while (i < all.length && all[i].role === "user") i++;
    setVisibleCount(Math.min(i, all.length));
    for (; i < all.length; i++) {
      // Paced by how much the PREVIOUS bubble gave them to read, so a long list is not chased by
      // the next message a third of a second later. See `revealDelay`.
      await sleep(revealDelay(all[i - 1]?.content ?? all[i]?.content ?? ""));
      setVisibleCount(i + 1);
    }
    setVisibleCount(all.length);
    staggering.current = false;
    setThinking(null);
  };

  // ---- actions -----------------------------------------------------------

  const createSimulation = async () => {
    setCreating(true);
    try {
      const next = await http.post<SimulationPayload>("/chatbot/simulations", {
        whatsapp_account: setup.whatsapp_account,
        phone: setup.phone,
        profile_name: setup.profile_name,
        language: setup.language,
        metadata: { source: "test_console_ui" },
      });
      // A new conversation must not inherit the previous one's history, emotion,
      // persona or journey.
      setPayload(next);
      setComposer("");
      setOptimisticUser(null);
      setThinking(null);
      setVisibleCount(0);
      stopPlayback();
      Object.values(ttsCache).forEach((u) => URL.revokeObjectURL(u));
      setTtsCache({});
      setTtsLoading({});
      toast.push("New conversation started", "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to start the simulation"), "critical");
    } finally {
      setCreating(false);
    }
  };

  const sendTurn = async (text: string, buttonId?: string) => {
    if (!simulation || sending) return;
    const body = text.trim();
    if (!body) return;
    if (!buttonId) setComposer("");
    setSending(true);
    const prevCount = messages.length;
    setOptimisticUser(body);
    setThinking({ active: true, steps: [], totalMs: 0, sources: 0 });
    const streamed = startChainStream(body, buttonId ? "test_console_button" : "test_console_live");
    try {
      const next = await http.post<SimulationPayload>(
        `/chatbot/simulations/${simulation.id}/turns`,
        buttonId
          ? { button_id: buttonId, button_title: body, metadata: { ui_sent_at: new Date().toISOString() } }
          : { message: body, metadata: { ui_sent_at: new Date().toISOString() } }
      );
      await streamed;
      setThinking((t) => (t ? { ...t, active: false } : t));
      await revealTurn(next, prevCount);
    } catch (err) {
      toast.push(errMessage(err, buttonId ? "Failed to send the button tap" : "Failed to send the turn"), "critical");
      setOptimisticUser(null);
      setThinking(null);
    } finally {
      setSending(false);
    }
  };

  const sendMediaTurn = async (blob: Blob, filename: string, kind: "audio" | "document") => {
    if (!simulation || sending) return;
    const typed = composer.trim();
    setComposer("");
    setSending(true);
    const prevCount = messages.length;
    // WhatsApp-style: preview the local file straight away, not the transcript.
    const previewKind = kind === "audio" ? "audio" : blob.type.startsWith("image/") ? "image" : "document";
    clearOptimisticMedia();
    setOptimisticMedia({ url: URL.createObjectURL(blob), kind: previewKind, filename, caption: typed });
    setThinking({ active: true, steps: [], totalMs: 0, sources: 0 });
    try {
      const form = new FormData();
      form.append("file", blob, filename);
      form.append("kind", kind);
      if (typed) form.append("message", typed);
      const next = await http.upload<SimulationPayload>(`/chatbot/simulations/${simulation.id}/media-turns`, form);
      setThinking((t) => (t ? { ...t, active: false } : t));
      await revealTurn(next, prevCount);
    } catch (err) {
      toast.push(
        errMessage(err, kind === "audio" ? "Failed to process the voice message" : "Failed to process the attachment"),
        "critical"
      );
      clearOptimisticMedia();
      setThinking(null);
    } finally {
      setSending(false);
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop(); // onstop cleans up and auto-sends
    } else {
      releaseStream();
      setRecording(false);
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      stopRecording();
      return;
    }
    if (!simulation || sending) return;
    // getUserMedia is refused outright on a plain-HTTP remote host, so say that
    // rather than letting the browser report a misleading permission error.
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      toast.push(
        "Microphone needs a secure page. Open the console at http://localhost:8100 (SSH-forward the port) or over HTTPS.",
        "critical"
      );
      return;
    }
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.push("Voice recording is not supported in this browser.", "critical");
      return;
    }

    let micStream: MediaStream;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as Error)?.name ?? "";
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
        toast.push(
          "Microphone permission is blocked. Allow the microphone from the address bar, then click the mic again.",
          "critical"
        );
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        toast.push("No microphone was found on this device.", "critical");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        toast.push("The microphone is in use by another app. Close it and try again.", "critical");
      } else {
        toast.push("Could not access the microphone. Check the browser and system permissions.", "critical");
      }
      return;
    }

    streamRef.current = micStream;
    const mime = pickRecordingMime();
    chunksRef.current = [];
    try {
      recorderRef.current = mime ? new MediaRecorder(micStream, { mimeType: mime }) : new MediaRecorder(micStream);
    } catch {
      toast.push("Voice recording is not supported in this browser.", "critical");
      releaseStream();
      return;
    }
    recorderRef.current.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorderRef.current.onstop = () => {
      const type = recorderRef.current?.mimeType || "audio/webm";
      releaseStream();
      setRecording(false);
      if (!chunksRef.current.length) return;
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      const ext = type.includes("ogg") ? "ogg" : type.includes("mp4") ? "mp4" : "webm";
      void sendMediaTurn(blob, `voice-note.${ext}`, "audio");
    };
    recorderRef.current.start();
    setRecording(true);
    setRecordSeconds(0);
    timerRef.current = window.setInterval(() => setRecordSeconds((s) => s + 1), 1000);
  };

  const onFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // so re-picking the same file fires change again
    if (!file) return;
    if (file.size > MAX_MEDIA_BYTES) {
      toast.push("File is too large. The maximum is 16MB.", "critical");
      return;
    }
    void sendMediaTurn(file, file.name, file.type.startsWith("audio/") ? "audio" : "document");
  };

  const refreshSimulation = async () => {
    if (!simulation) return;
    setRefreshing(true);
    try {
      setPayload(await http.get<SimulationPayload>(`/chatbot/simulations/${simulation.id}`));
    } catch (err) {
      toast.push(errMessage(err, "Failed to refresh the simulation"), "critical");
    } finally {
      setRefreshing(false);
    }
  };

  const promoteToEval = async () => {
    if (!simulation) return;
    setActing(true);
    try {
      await http.post(`/chatbot/simulations/${simulation.id}/promote`, {
        message_id: latestBotMessage?.id ?? latestUserMessage?.id,
        note: evalNote || "Promoted from Chatbot Test Console",
      });
      toast.push("Eval candidate created", "good");
      await refreshSimulation();
    } catch (err) {
      toast.push(errMessage(err, "Failed to promote the turn"), "critical");
    } finally {
      setActing(false);
    }
  };

  const flagForReview = async () => {
    if (!simulation) return;
    setActing(true);
    try {
      await http.post(`/chatbot/simulations/${simulation.id}/review`, {
        message_id: latestBotMessage?.id ?? latestUserMessage?.id,
        note: evalNote || "Needs review from Chatbot Test Console",
      });
      toast.push("Review item created", "good");
      await refreshSimulation();
    } catch (err) {
      toast.push(errMessage(err, "Failed to create the review item"), "critical");
    } finally {
      setActing(false);
    }
  };

  const copyLatestTurn = async () => {
    const text = latestUserMessage?.content || composer;
    if (!text) return;
    await navigator.clipboard.writeText(text);
    toast.push("Copied", "good");
  };

  const openInModelLab = () => {
    const text = latestUserMessage?.content || composer;
    navigate(`/chatbot/model-lab${qs({ message: text })}`);
  };

  // ---- per-message analysis ---------------------------------------------
  const [details, setDetails] = useState<{
    open: boolean;
    loading: boolean;
    error: string;
    data: LabRun | null;
    question: string;
  }>({ open: false, loading: false, error: "", data: null, question: "" });

  const openDetails = async (message: SimulationMessage) => {
    const idx = messages.findIndex((m) => m.id === message.id);
    let question = latestUserMessage?.content ?? "";
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === "user" && (messages[i].content || "").trim()) {
        question = messages[i].content;
        break;
      }
    }
    setDetails({ open: true, loading: true, error: "", data: null, question });
    try {
      const run = await http.post<LabRun>("/chatbot/model-lab/run", {
        message: question,
        message_type: "text",
        metadata: { source: "test_console_details" },
      });
      setDetails((d) => ({ ...d, loading: false, data: run }));
    } catch (err) {
      setDetails((d) => ({ ...d, loading: false, error: errMessage(err, "Could not load the analysis") }));
    }
  };

  const recordClock = `${String(Math.floor(recordSeconds / 60)).padStart(2, "0")}:${String(recordSeconds % 60).padStart(2, "0")}`;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Test console</h1>
          <div className="sub">Drive the live chatbot path without sending a WhatsApp message.</div>
        </div>
        <div className="page-actions">
          <button className="btn" disabled={!canWrite || creating} onClick={() => void createSimulation()}>
            {creating ? <Spinner /> : "▷"} New conversation
          </button>
          <button className="btn" disabled={!simulation || refreshing} onClick={() => void refreshSimulation()}>
            {refreshing ? <Spinner /> : "⟲"} Refresh
          </button>
          {canLab && (
            <button className="btn" disabled={!simulation} onClick={openInModelLab}>
              ⚗ Model Lab
            </button>
          )}
        </div>
      </div>

      <ErrorBanner error={accountsError} what="Could not load the WhatsApp accounts" />
      {!canWrite && <Banner tone="info">Running a simulation needs Test Console access. Ask an administrator.</Banner>}

      <div className="console">
        <div className="stack">
          <div className="card">
            <div className="row" style={{ marginBottom: "var(--sp-3)" }}>
              <h3 style={{ flex: 1 }}>Setup</h3>
              <Badge tone="neutral">{simulation?.status ?? "new"}</Badge>
            </div>
            <Field label="Account" hint="Leave on auto to use the local simulation account.">
              <select
                value={setup.whatsapp_account}
                onChange={(e) => setSetup({ ...setup, whatsapp_account: e.target.value })}
              >
                <option value="">Auto / local simulation account</option>
                {accounts.map((a) => (
                  <option key={a.id ?? a.name} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Display phone">
              <input value={setup.phone} onChange={(e) => setSetup({ ...setup, phone: e.target.value })} />
            </Field>
            <Field label="Profile">
              <input value={setup.profile_name} onChange={(e) => setSetup({ ...setup, profile_name: e.target.value })} />
            </Field>
            <Field label="Language" hint="en, hi, hing, bn, bnro">
              <input value={setup.language} onChange={(e) => setSetup({ ...setup, language: e.target.value })} />
            </Field>
            <button className="btn primary block" disabled={!canWrite || creating} onClick={() => void createSimulation()}>
              {creating ? <Spinner /> : "▷"} Start simulation
            </button>
          </div>

          <div className="card">
            <h3 style={{ marginBottom: "var(--sp-3)" }}>Actions</h3>
            <textarea rows={3} placeholder="Eval or review note" value={evalNote} onChange={(e) => setEvalNote(e.target.value)} />
            <div className="grid c2" style={{ marginTop: "var(--sp-3)" }}>
              <button className="btn sm" disabled={!simulation || !canWrite || acting} onClick={() => void promoteToEval()}>
                ✓ Eval
              </button>
              <button className="btn sm" disabled={!simulation || !canWrite || acting} onClick={() => void flagForReview()}>
                ⚑ Review
              </button>
              <button className="btn sm" disabled={!latestUserMessage} onClick={() => void copyLatestTurn()}>
                ⧉ Copy
              </button>
              {canLab && (
                <button className="btn sm" disabled={!simulation} onClick={openInModelLab}>
                  ⚗ Lab
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="console-chat">
          <div className="card-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3>{simulation?.profile_name || "Test conversation"}</h3>
              <div className="muted truncate" style={{ fontSize: 12 }}>
                {simulation?.display_phone || setup.phone} · {simulation?.whatsapp_account || setup.whatsapp_account || "auto"}
              </div>
            </div>
          </div>

          <div className="scroll" ref={scrollRef}>
            {messages.length === 0 && (
              <Empty title="No turns yet" body="Start a simulation, then send the first message." icon="▷" />
            )}

            {displayed.map((m) => {
              const mine = m.role === "user";
              const buttons = m.interactive_data?.buttons ?? [];
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className={`bubble ${mine ? "outbound" : "bot"}`}
                >
                  {m.media_url && (
                    <div style={{ marginBottom: 4 }}>
                      {m.type === "image" && mediaUrls[m.id] ? (
                        <img className="media" src={mediaUrls[m.id]} alt={m.media_filename || "attachment"} />
                      ) : m.type === "audio" && mediaUrls[m.id] ? (
                        <audio className="media" src={mediaUrls[m.id]} controls />
                      ) : mediaUrls[m.id] ? (
                        <a href={mediaUrls[m.id]} download={m.media_filename || "document"} target="_blank" rel="noreferrer">
                          ▤ {m.media_filename || "Document"}
                        </a>
                      ) : mediaLoading[m.id] ? (
                        <span className="muted">
                          <Spinner /> Loading {m.type}…
                        </span>
                      ) : (
                        <span className="muted">[{m.type}]</span>
                      )}
                    </div>
                  )}

                  <div className="prewrap">{m.content || `[${m.type}]`}</div>

                  {isBot(m) && buttons.length > 0 && m.id === latestBotMessage?.id && (
                    <div className="chips" style={{ marginTop: "var(--sp-2)" }}>
                      {buttons.map((b) => (
                        <button
                          key={b.id}
                          className="btn sm"
                          disabled={sending}
                          onClick={() => void sendTurn(b.title, b.id)}
                        >
                          {b.title}
                        </button>
                      ))}
                    </div>
                  )}

                  <span className="when">
                    {m.role} · {m.status}
                  </span>

                  {isBot(m) && (
                    <div className="chips" style={{ marginTop: "var(--sp-2)" }}>
                      {isVoiceReply(m, messages) && (
                        <button
                          className="btn sm ghost"
                          disabled={ttsLoading[m.id]}
                          title={ttsPlaying === m.id ? "Stop playback" : "Play this reply as voice"}
                          onClick={() => void playReply(m)}
                        >
                          {ttsLoading[m.id] ? <Spinner /> : ttsPlaying === m.id ? "⏸" : "🔊"}{" "}
                          {ttsPlaying === m.id ? "Playing…" : "Play voice"}
                        </button>
                      )}
                      <button className="btn sm ghost" onClick={() => void openDetails(m)}>
                        ⚗ Analysis · timing · sources
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}

            {(optimisticUser || optimisticMedia) && (
              <div className="bubble outbound">
                {optimisticMedia && (
                  <div style={{ marginBottom: 4 }}>
                    {optimisticMedia.kind === "image" ? (
                      <img className="media" src={optimisticMedia.url} alt={optimisticMedia.filename} />
                    ) : optimisticMedia.kind === "audio" ? (
                      <audio className="media" src={optimisticMedia.url} controls />
                    ) : (
                      <span>▤ {optimisticMedia.filename}</span>
                    )}
                  </div>
                )}
                <div className="prewrap">{optimisticMedia ? optimisticMedia.caption : optimisticUser}</div>
              </div>
            )}

            {thinking && (
              <div className="think">
                <div className="row muted" style={{ fontSize: 12 }}>
                  {thinking.active ? <Spinner /> : <span>✓</span>}
                  <span>{thinking.active ? "Thinking…" : "Analysis complete"}</span>
                  {thinking.totalMs > 0 && <span className="num">· {fmtMs(thinking.totalMs)} total</span>}
                  {thinking.sources > 0 && <span>· {thinking.sources} sources</span>}
                </div>
                {thinking.steps.length === 0 && <div className="muted" style={{ fontSize: 12 }}>Starting…</div>}
                {thinking.steps.map((s, i) => (
                  <div key={`${s.stage}-${i}`}>
                    <div className="step">
                      <span>{s.status === "done" ? "✓" : "•"}</span>
                      <span className={s.status === "done" ? "" : "muted"}>{stageLabel(s.stage)}</span>
                      {s.latency_ms != null && <span className="ms">{fmtMs(s.latency_ms)}</span>}
                    </div>
                    {s.note && (
                      <div className="muted" style={{ fontSize: 11, marginLeft: 18 }}>
                        {s.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {recording && (
            <div className="rec">
              <span className="pulse" />
              <span>Recording… {recordClock}</span>
              <span className="spacer" />
              <button className="btn sm danger" onClick={stopRecording}>
                ■ Stop and send
              </button>
            </div>
          )}

          <form
            className="composer"
            onSubmit={(e) => {
              e.preventDefault();
              void sendTurn(composer);
            }}
          >
            <div className="row">
              <input ref={fileRef} type="file" className="sr-only" accept="audio/*,image/*,.pdf,.doc,.docx,.txt" onChange={onFileSelected} />
              <button
                type="button"
                className="btn icon"
                title="Attach a document or file"
                disabled={!simulation || sending || recording}
                onClick={() => fileRef.current?.click()}
              >
                📎
              </button>
              <button
                type="button"
                className={`btn icon${recording ? " danger" : ""}`}
                title={recording ? "Stop recording and send" : "Record a voice message"}
                disabled={!simulation || (sending && !recording)}
                onClick={() => void toggleRecording()}
              >
                {recording ? "■" : "🎙"}
              </button>
              <textarea
                value={composer}
                disabled={!simulation || sending || recording}
                placeholder={
                  recording
                    ? "Recording… tap stop to send"
                    : "Type a message and press Enter to send (Shift+Enter for a new line)"
                }
                onChange={(e) => setComposer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void sendTurn(composer);
                  }
                }}
              />
              <button className="btn primary icon" type="submit" disabled={!simulation || !composer.trim() || sending || recording}>
                {sending ? <Spinner /> : "➤"}
              </button>
            </div>
          </form>
        </div>

        <DebugPanel payload={payload} latestUserMessage={latestUserMessage} />
      </div>

      <Dialog
        open={details.open}
        onClose={() => setDetails((d) => ({ ...d, open: false }))}
        title="How the assistant analysed this"
        wide
      >
        <p className="muted truncate">“{details.question}”</p>
        {details.loading && (
          <div className="row">
            <Spinner /> Running the intelligence pipeline…
          </div>
        )}
        {!details.loading && details.error && <Banner tone="critical">{details.error}</Banner>}
        {!details.loading && details.data && <TurnAnalysis data={details.data} />}
        <DialogFooter>
          <button className="btn" onClick={() => setDetails((d) => ({ ...d, open: false }))}>
            Close
          </button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
